import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, Download, FileSpreadsheet, Plus, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { applyWorksheetStyle, styleWorkbook } from "../excel";
import { CatalogSelect, useCatalogs } from "./boms";
import { formatDate } from "./catalogs";
import { PageHeader } from "../components/shell";
import {
  Button,
  CatalogLabel,
  ConfirmDialog,
  EmptyState,
  FormField,
  Modal,
  NumericInput,
  Pagination,
  Panel,
  SearchBox,
} from "../components/ui";
import { formatNumber, normalizeNumber } from "../numbers";
import type { InventoryItem, InventoryTransfer, PageMeta } from "../types";

type InventoryResponse = { items: InventoryItem[]; meta: PageMeta };
type InventoryTransferResponse = { items: InventoryTransfer[]; meta: PageMeta };
type ImportRow = {
  rowNo: number;
  materialCode: string;
  materialName: string;
  warehouseCode: string;
  warehouseName: string;
  unitCode: string;
  unitName: string;
  quantity: string;
  snapshotAt: string;
  note: string;
};
type ImportIssue = { rowNo: number; message: string };
type InventoryCatalogCandidate = {
  type: "material" | "warehouse" | "unit";
  code: string;
  name: string;
  rowNos: number[];
};
type MissingCatalogResponse = { items: InventoryCatalogCandidate[]; issues: ImportIssue[] };

const inventoryCatalogLabels: Record<InventoryCatalogCandidate["type"], string> = {
  material: "Vật tư",
  warehouse: "Kho",
  unit: "Đơn vị tính",
};

const inventoryTemplateColumns = [
  "Mã vật tư",
  "Tên vật tư",
  "Mã kho",
  "Tên kho",
  "Mã ĐVT",
  "Tên ĐVT",
  "Tồn kho",
  "Thời điểm chốt",
  "Ghi chú",
];

function normalizedHeader(value: unknown): string {
  return String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("vi")
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9]/g, "");
}

function nowInHcmInput() {
  return new Date(Date.now() + 7 * 60 * 60 * 1000).toISOString().slice(0, 16);
}

function toHcmIso(value: unknown): string | null {
  const source = String(value ?? "").trim();
  if (!source) return null;
  if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/i.test(source)) {
    const date = new Date(source);
    return Number.isNaN(date.valueOf()) ? null : date.toISOString();
  }
  const iso = source.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  const vietnamese = source.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  const parts = iso
    ? { year: iso[1], month: iso[2], day: iso[3], hour: iso[4] ?? "00", minute: iso[5] ?? "00", second: iso[6] ?? "00" }
    : vietnamese
      ? { year: vietnamese[3], month: (vietnamese[2] ?? "").padStart(2, "0"), day: (vietnamese[1] ?? "").padStart(2, "0"), hour: vietnamese[4] ?? "00", minute: vietnamese[5] ?? "00", second: vietnamese[6] ?? "00" }
      : null;
  if (!parts) return null;
  const { year = "", month = "", day = "", hour = "00", minute = "00", second = "00" } = parts;
  if (!year || !month || !day) return null;
  const date = new Date(`${year}-${month}-${day}T${hour.padStart(2, "0")}:${minute}:${second}+07:00`);
  return Number.isNaN(date.valueOf()) ? null : date.toISOString();
}

function parseQuantity(value: unknown): string | null {
  return normalizeNumber(value);
}

function issueKey(issue: ImportIssue) {
  return `${issue.rowNo}:${issue.message}`;
}

export function InventoryPage({ roles }: { roles: string[] }) {
  const [tab, setTab] = useState<"stock" | "transfer">("stock");
  const [search, setSearch] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const catalogs = useCatalogs(["warehouse"]);
  const queryClient = useQueryClient();
  const inventory = useQuery({
    queryKey: ["inventory", search, warehouseId, page],
    queryFn: () => api<InventoryResponse>(`/inventory?page=${page}&pageSize=25&q=${encodeURIComponent(search)}${warehouseId ? `&warehouseId=${warehouseId}` : ""}`),
  });
  useEffect(() => setPage(1), [search, warehouseId]);
  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    void queryClient.invalidateQueries({ queryKey: ["inventory-transfers"] });
  }
  const canInitialImport = roles.includes("superadmin") && inventory.data?.meta.total === 0;
  return (
    <div className="page">
      <PageHeader
        title="Kho & tồn kho"
        actions={
          tab === "stock" ? <div className="inventory-actions">
            <InventoryTemplateButton />
            <InventoryExportButton warehouseId={warehouseId} />
            {canInitialImport && <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload size={16} /> Nhập tồn kho ban đầu
            </Button>}
          </div> : <Button onClick={() => setTransferOpen(true)}><ArrowRightLeft size={16} /> Chuyển kho</Button>
        }
      />
      <div className="catalog-tabs inventory-tabs" role="tablist" aria-label="Quản lý kho">
        <button type="button" role="tab" aria-selected={tab === "stock"} className={tab === "stock" ? "selected" : ""} onClick={() => setTab("stock")}>Tồn kho</button>
        <button type="button" role="tab" aria-selected={tab === "transfer"} className={tab === "transfer" ? "selected" : ""} onClick={() => setTab("transfer")}>Điều chuyển kho</button>
      </div>
      {tab === "stock" ? <Panel>
        <div className="catalog-guidance">
          <strong>Tồn kho hiện tại</strong>
          <span>Số dư được lập từ tệp tồn kho ban đầu và tự cập nhật sau khi xác nhận sử dụng hoặc điều chuyển kho. Tồn kho không sửa trực tiếp để bảo toàn lịch sử.</span>
        </div>
        <div className="table-toolbar">
          <SearchBox value={search} onChange={setSearch} placeholder="Tìm vật tư hoặc kho..." />
          <label className="filter-select">
            <span>Kho:</span>
            <select value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}>
              <option value="">Tất cả kho</option>
              {(catalogs?.warehouse ?? []).map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
            </select>
          </label>
        </div>
        {inventory.isLoading ? (
          <div className="loading-space">Đang tải tồn kho…</div>
        ) : !inventory.data?.items.length ? (
          <EmptyState
            title="Chưa có số liệu tồn kho"
            detail={roles.includes("superadmin") ? "Superadmin có thể nhập tệp tồn kho ban đầu một lần. Sau khi chốt, tồn kho chỉ đổi theo phiếu sử dụng và điều chuyển kho." : "Tồn kho ban đầu do Superadmin nhập một lần; sau đó số tồn chỉ đổi theo phiếu sử dụng và điều chuyển kho."}
            action={canInitialImport ? <Button onClick={() => setImportOpen(true)}><Upload size={16} /> Nhập tồn kho ban đầu</Button> : undefined}
          />
        ) : (
          <>
            <div className="table-scroll">
              <table>
                <thead><tr><th>Vật tư</th><th>Kho</th><th>ĐVT</th><th>Số chốt</th><th>Còn lại</th><th>Thời điểm chốt</th><th>Ghi chú</th></tr></thead>
                <tbody>
                  {inventory.data.items.map((item) => (
                    <tr key={item.id}>
                      <td><CatalogLabel code={item.material.code} name={item.material.name} /></td>
                      <td>{item.warehouse.name}</td>
                      <td>{item.unit.name}</td>
                      <td className="num-cell">{formatNumber(item.quantity)}</td>
                      <td className="num-cell strong">{formatNumber(item.available)}</td>
                      <td>{formatDate(item.snapshotAt)}</td>
                      <td>{item.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={inventory.data.meta} onPage={setPage} />
          </>
        )}
      </Panel> : <InventoryTransferHistory warehouseId={warehouseId} />}
      <InventoryImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={() => { setImportOpen(false); refresh(); }} />
      <InventoryTransferModal open={transferOpen} onClose={() => setTransferOpen(false)} onTransferred={() => { setTransferOpen(false); refresh(); }} />
    </div>
  );
}

function InventoryTransferHistory({ warehouseId }: { warehouseId: string }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const transfers = useQuery({
    queryKey: ["inventory-transfers", search, warehouseId, page],
    queryFn: () => api<InventoryTransferResponse>(`/inventory/transfers?page=${page}&pageSize=25&q=${encodeURIComponent(search)}${warehouseId ? `&warehouseId=${warehouseId}` : ""}`),
  });
  useEffect(() => setPage(1), [search, warehouseId]);
  return <Panel title="Lịch sử chuyển kho">
    <div className="table-toolbar">
      <SearchBox value={search} onChange={setSearch} placeholder="Tìm vật tư, kho hoặc ghi chú..." />
    </div>
    {transfers.isLoading ? (
      <div className="loading-space">Đang tải lịch sử chuyển kho…</div>
    ) : !transfers.data?.items.length ? (
      <EmptyState title="Chưa có phiếu chuyển kho" detail="Sau khi chuyển kho, thông tin nguồn, kho nhận và số lượng sẽ được lưu tại đây." />
    ) : <>
      <div className="table-scroll">
        <table>
          <thead><tr><th>Thời điểm</th><th>Vật tư</th><th>Từ kho</th><th>Đến kho</th><th>ĐVT</th><th>Số lượng</th><th>Người chuyển</th><th>Ghi chú</th></tr></thead>
          <tbody>{transfers.data.items.map((transfer) => <tr key={transfer.id}>
            <td>{formatDate(transfer.occurredAt)}</td>
            <td><CatalogLabel code={transfer.material.code} name={transfer.material.name} /></td>
            <td>{transfer.sourceWarehouse.name}</td>
            <td>{transfer.destinationWarehouse.name}</td>
            <td>{transfer.unit.name}</td>
            <td className="num-cell strong">{formatNumber(transfer.quantity)}</td>
            <td>{transfer.createdBy.displayName}</td>
            <td>{transfer.note || "—"}</td>
          </tr>)}</tbody>
        </table>
      </div>
      <Pagination meta={transfers.data.meta} onPage={setPage} />
    </>}
  </Panel>;
}

function InventoryTransferModal({ open, onClose, onTransferred }: { open: boolean; onClose(): void; onTransferred(): void }) {
  const catalogs = useCatalogs(["material", "warehouse", "unit"]);
  const [materialId, setMaterialId] = useState("");
  const [sourceWarehouseId, setSourceWarehouseId] = useState("");
  const [destinationWarehouseId, setDestinationWarehouseId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setMaterialId("");
    setSourceWarehouseId("");
    setDestinationWarehouseId("");
    setUnitId("");
    setQuantity("");
    setNote("");
    setError("");
    setConfirmOpen(false);
    setSaving(false);
  }, [open]);
  const selected = (type: "material" | "warehouse" | "unit", id: string) => (catalogs?.[type] ?? []).find((catalog) => catalog.id === id);
  const material = selected("material", materialId);
  const sourceWarehouse = selected("warehouse", sourceWarehouseId);
  const destinationWarehouse = selected("warehouse", destinationWarehouseId);
  const unit = selected("unit", unitId);
  const materialUnits = material?.defaultUnitId
    ? (catalogs?.unit ?? []).filter((item) => item.id === material.defaultUnitId)
    : [];
  function selectMaterial(id: string) {
    setMaterialId(id);
    const selectedMaterial = selected("material", id);
    setUnitId(selectedMaterial?.defaultUnitId ?? "");
  }
  function requestConfirmation(event: React.FormEvent) {
    event.preventDefault();
    const normalizedQuantity = parseQuantity(quantity);
    const number = Number(normalizedQuantity);
    if (!normalizedQuantity || !Number.isFinite(number) || number <= 0) {
      setError("Vui lòng nhập số lượng chuyển lớn hơn 0.");
      return;
    }
    if (sourceWarehouseId === destinationWarehouseId) {
      setError("Kho chuyển đi và kho nhận không được trùng nhau.");
      return;
    }
    setError("");
    setConfirmOpen(true);
  }
  async function transfer() {
    setSaving(true);
    setError("");
    try {
      await api("/inventory/transfers", {
        method: "POST",
        body: JSON.stringify({
          materialId,
          sourceWarehouseId,
          destinationWarehouseId,
          unitId,
          quantity: parseQuantity(quantity),
          note,
        }),
      });
      setConfirmOpen(false);
      onTransferred();
    } catch (cause) {
      setConfirmOpen(false);
      setError(cause instanceof Error ? cause.message : "Không thể chuyển kho lúc này.");
    } finally {
      setSaving(false);
    }
  }
  return <>
    <Modal open={open && !confirmOpen} title="Chuyển kho" onClose={onClose}>
      <form className="modal-form" onSubmit={requestConfirmation}>
        <p className="muted">Hệ thống chỉ ghi nhận phiếu khi kho chuyển còn đủ tồn cùng đơn vị tính. Số dư tại hai kho sẽ được cập nhật ngay sau khi xác nhận.</p>
        <CatalogSelect label="Vật tư" value={materialId} onChange={selectMaterial} items={catalogs?.material ?? []} required />
        <div className="form-grid">
          <CatalogSelect label="Từ kho" value={sourceWarehouseId} onChange={setSourceWarehouseId} items={catalogs?.warehouse ?? []} required />
          <CatalogSelect label="Đến kho" value={destinationWarehouseId} onChange={setDestinationWarehouseId} items={catalogs?.warehouse ?? []} required />
        </div>
        <div className="form-grid">
          <CatalogSelect label="Đơn vị tính" value={unitId} onChange={setUnitId} items={materialUnits} required disabled />
          <FormField label="Số lượng chuyển"><NumericInput required value={quantity} onValueChange={setQuantity} placeholder="Ví dụ: 1.250,5" /></FormField>
        </div>
        <FormField label="Ghi chú"><input value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} /></FormField>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Hủy</Button>
          <Button type="submit" disabled={saving || !catalogs}>Xác nhận thông tin</Button>
        </div>
      </form>
    </Modal>
    <ConfirmDialog
      open={open && confirmOpen}
      title="Xác nhận chuyển kho"
      message={`Chuyển ${quantity || "0"} ${unit?.name ?? ""} vật tư “${material?.name ?? ""}” từ kho “${sourceWarehouse?.name ?? ""}” đến kho “${destinationWarehouse?.name ?? ""}”?`}
      confirmLabel="Chuyển kho"
      variant="primary"
      pending={saving}
      onClose={() => setConfirmOpen(false)}
      onConfirm={() => void transfer()}
    />
  </>;
}

function InventorySnapshotModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const catalogs = useCatalogs(["material", "warehouse", "unit"]);
  const [materialId, setMaterialId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [snapshotAt, setSnapshotAt] = useState(nowInHcmInput());
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setMaterialId(""); setWarehouseId(""); setUnitId(""); setQuantity(""); setSnapshotAt(nowInHcmInput()); setNote(""); setError("");
  }, [open]);
  const selectedMaterial = (catalogs?.material ?? []).find((material) => material.id === materialId);
  const materialUnits = selectedMaterial?.defaultUnitId
    ? (catalogs?.unit ?? []).filter((unit) => unit.id === selectedMaterial.defaultUnitId)
    : [];
  function selectMaterial(id: string) {
    setMaterialId(id);
    const material = (catalogs?.material ?? []).find((item) => item.id === id);
    setUnitId(material?.defaultUnitId ?? "");
  }
  async function save(event: React.FormEvent) {
    event.preventDefault();
    const snapshotIso = toHcmIso(snapshotAt);
    const validQuantity = parseQuantity(quantity);
    if (!snapshotIso || !validQuantity) { setError("Vui lòng nhập số lượng từ 0 trở lên và thời điểm chốt hợp lệ."); return; }
    setSaving(true); setError("");
    try {
      await api("/inventory/snapshots", { method: "POST", body: JSON.stringify({ materialId, warehouseId, unitId, quantity: validQuantity, snapshotAt: snapshotIso, note }) });
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể lưu số liệu tồn kho.");
    } finally { setSaving(false); }
  }
  return (
    <Modal open={open} title="Chốt tồn kho" onClose={onClose}>
      <form className="modal-form" onSubmit={save}>
        <p className="muted">Nếu trùng vật tư, kho, đơn vị tính và thời điểm chốt, hệ thống sẽ cập nhật lại chính dòng chốt đó.</p>
        <CatalogSelect label="Vật tư" value={materialId} onChange={selectMaterial} items={catalogs?.material ?? []} required />
        <CatalogSelect label="Kho" value={warehouseId} onChange={setWarehouseId} items={catalogs?.warehouse ?? []} required />
        <CatalogSelect label="Đơn vị tính" value={unitId} onChange={setUnitId} items={materialUnits} required disabled />
        {materialId && !selectedMaterial?.defaultUnitId && <p className="form-error">Vật tư này chưa có đơn vị tính chuẩn. Hãy cập nhật tại Danh mục vật tư.</p>}
        <div className="form-grid">
          <FormField label="Số lượng tồn"><input required inputMode="decimal" value={quantity} onChange={(event) => setQuantity(event.target.value)} placeholder="Ví dụ: 1250.5" /></FormField>
          <FormField label="Thời điểm chốt"><input required type="datetime-local" value={snapshotAt} onChange={(event) => setSnapshotAt(event.target.value)} /></FormField>
        </div>
        <FormField label="Ghi chú"><input value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} /></FormField>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions"><Button type="button" variant="outline" onClick={onClose} disabled={saving}>Hủy</Button><Button type="submit" disabled={saving || !catalogs}>Lưu số chốt</Button></div>
      </form>
    </Modal>
  );
}

function DeleteInventoryButton({ item, onDeleted }: { item: InventoryItem; onDeleted(): void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function remove() {
    setBusy(true); setError("");
    try { await api(`/inventory/snapshots/${item.id}`, { method: "DELETE" }); setOpen(false); onDeleted(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Không thể xóa số chốt tồn kho."); }
    finally { setBusy(false); }
  }
  return <>
    <button type="button" title="Xóa lần chốt" aria-label={`Xóa tồn kho ${item.material.name}`} onClick={() => setOpen(true)}><Trash2 size={17} /></button>
    <ConfirmDialog open={open} title={error ? "Không thể xóa lần chốt" : "Xóa lần chốt tồn kho"} message={error || `Bạn có chắc muốn xóa lần chốt của “${item.material.name}” tại kho “${item.warehouse.name}” không?`} confirmLabel="Xóa lần chốt" pending={busy} onClose={() => setOpen(false)} onConfirm={() => void remove()} />
  </>;
}

function InventoryImportModal({ open, onClose, onImported }: { open: boolean; onClose(): void; onImported(): void }) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [issues, setIssues] = useState<ImportIssue[]>([]);
  const [catalogIssues, setCatalogIssues] = useState<ImportIssue[]>([]);
  const [missingCatalogs, setMissingCatalogs] = useState<InventoryCatalogCandidate[]>([]);
  const [checked, setChecked] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (!open) return; setFileName(""); setRows([]); setIssues([]); setCatalogIssues([]); setMissingCatalogs([]); setChecked(false); setValidationMessage(""); setBusy(false); setError(""); }, [open]);
  const localIssues = useMemo(() => validateImportRows(rows), [rows]);
  const allIssues = useMemo(() => {
    const map = new Map<string, ImportIssue>();
    [...localIssues, ...catalogIssues, ...issues].forEach((issue) => map.set(issueKey(issue), issue));
    return [...map.values()].sort((left, right) => left.rowNo - right.rowNo);
  }, [catalogIssues, issues, localIssues]);

  async function discoverMissingCatalogs(sourceRows: ImportRow[]) {
    const catalogRows = sourceRows.filter((row) => row.materialCode && row.warehouseCode && row.unitCode);
    if (!catalogRows.length) return;
    const response = await api<MissingCatalogResponse>("/inventory/missing-catalogs", { method: "POST", body: JSON.stringify({ rows: catalogRows }) });
    setMissingCatalogs(response.items);
    setCatalogIssues(response.issues);
  }
  async function readFile(file?: File) {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { setError("Tệp Excel vượt quá giới hạn 20 MB."); return; }
    setBusy(true); setError("");
    try {
      const XLSX = await import("xlsx");
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: false });
      const sheet = workbook.Sheets[workbook.SheetNames[0] ?? ""];
      if (!sheet) { setError("Tệp Excel không có trang tính để nhập."); return; }
      const records = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "", raw: false, dateNF: "yyyy-mm-dd hh:mm" });
      if (!records.length) { setError("Tệp Excel chưa có dòng dữ liệu."); return; }
      if (records.length > 5000) { setError("Mỗi lần nhập tối đa 5.000 dòng tồn kho."); return; }
      const nextRows = records.map((record, index) => {
        const values = new Map(Object.entries(record).map(([key, value]) => [normalizedHeader(key), value]));
        const value = (...keys: string[]) => keys.map((key) => values.get(key)).find((item) => item !== undefined) ?? "";
        const quantityValue = value("tonkho", "soluongton", "soluong");
        return {
          rowNo: index + 2,
          materialCode: String(value("mavattu", "mavt")).trim(),
          materialName: String(value("tenvattu", "tenvt")).trim(),
          warehouseCode: String(value("makho")).trim(),
          warehouseName: String(value("tenkho")).trim(),
          unitCode: String(value("madv t", "madv t", "madvt", "madonvitinh")).trim(),
          unitName: String(value("tendvt", "tendonvitinh")).trim(),
          quantity: parseQuantity(quantityValue) ?? String(quantityValue).trim(),
          snapshotAt: toHcmIso(value("thoidiemchot", "ngaychot", "thoidiem")) ?? "",
          note: String(value("ghichu")).trim(),
        };
      }).filter((row) => Boolean(row.materialCode || row.materialName || row.warehouseCode || row.warehouseName || row.unitCode || row.unitName || row.quantity || row.snapshotAt || row.note));
      setRows(nextRows); setFileName(file.name); setIssues([]); setCatalogIssues([]); setMissingCatalogs([]); setChecked(false); setValidationMessage("");
      try {
        await discoverMissingCatalogs(nextRows);
      } catch (cause) {
        setError(cause instanceof Error ? `Không thể kiểm tra danh mục: ${cause.message}` : "Không thể kiểm tra danh mục trong tệp.");
      }
    } catch { setError("Không thể đọc tệp Excel. Vui lòng dùng tệp .xlsx hợp lệ."); }
    finally { setBusy(false); }
  }
  async function validateOnServer() {
    if (!rows.length) return;
    if (localIssues.length || catalogIssues.length || missingCatalogs.length) { setChecked(false); return; }
    setBusy(true); setError("");
    try {
      const response = await api<{ validRows: number; issues: ImportIssue[] }>("/inventory/import", { method: "POST", body: JSON.stringify({ rows, dryRun: true }) });
      setIssues(response.issues);
      setChecked(response.issues.length === 0);
      setValidationMessage(response.issues.length ? "Tệp còn lỗi, hãy xử lý các dòng được liệt kê trước khi lưu." : `Đã kiểm tra ${response.validRows.toLocaleString("vi-VN")} dòng hợp lệ. Bạn có thể lưu tồn kho.`);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Không thể kiểm tra tệp tồn kho."); }
    finally { setBusy(false); }
  }
  async function save() {
    if (!checked || allIssues.length) return;
    setBusy(true); setError("");
    try {
      const response = await api<{ imported: number; issues: ImportIssue[] }>("/inventory/import", { method: "POST", body: JSON.stringify({ rows, dryRun: false }) });
      if (response.issues.length) { setIssues(response.issues); setChecked(false); return; }
      onImported();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Không thể lưu tồn kho từ Excel."); }
    finally { setBusy(false); }
  }

  async function addCatalogs(items: InventoryCatalogCandidate[]) {
    if (!items.length) return;
    setBusy(true);
    setError("");
    try {
      await api("/inventory/catalog-candidates", { method: "POST", body: JSON.stringify({ items }) });
      setIssues([]);
      setChecked(false);
      setValidationMessage("Đã bổ sung danh mục. Hãy kiểm tra tệp trước khi lưu tồn kho.");
      await discoverMissingCatalogs(rows);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể thêm danh mục từ tệp.");
    } finally {
      setBusy(false);
    }
  }
  return <Modal open={open} title="Nhập tồn kho từ Excel" onClose={onClose}>
    <div className="modal-form inventory-import-modal">
      <p className="muted">Tải tệp mẫu để có đúng các cột. Nếu mã vật tư, kho hoặc đơn vị tính chưa có, hệ thống sẽ đề nghị thêm danh mục từ tên trong tệp trước khi kiểm tra tồn kho.</p>
      <FormField label="Tệp Excel"><input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => void readFile(event.target.files?.[0])} /></FormField>
      {fileName && <p className="file-summary-inline"><FileSpreadsheet size={17} /> {fileName}: {rows.length.toLocaleString("vi-VN")} dòng</p>}
      {error && <p className="form-error">{error}</p>}
      {!!missingCatalogs.length && <section className="inventory-catalog-candidates">
        <div className="inventory-candidate-heading"><div><strong>Danh mục cần bổ sung</strong><span>Được nhận diện từ mã và tên trong tệp Excel.</span></div><Button type="button" disabled={busy} onClick={() => void addCatalogs(missingCatalogs)}><Plus size={16} /> Thêm tất cả ({missingCatalogs.length})</Button></div>
        <div className="table-scroll"><table><thead><tr><th>Loại danh mục</th><th>Thông tin từ tệp</th><th>Dòng dùng</th><th /></tr></thead><tbody>{missingCatalogs.map((item) => <tr key={`${item.type}:${item.code}`}><td>{inventoryCatalogLabels[item.type]}</td><td><CatalogLabel code={item.code} name={item.name} /></td><td>{item.rowNos.length}</td><td className="row-actions"><Button type="button" variant="outline" disabled={busy} onClick={() => void addCatalogs([item])}><Plus size={15} /> Thêm</Button></td></tr>)}</tbody></table></div>
      </section>}
      {!!allIssues.length && <div className="issues"><table><thead><tr><th>Dòng</th><th>Vấn đề cần xử lý</th></tr></thead><tbody>{allIssues.slice(0, 12).map((issue) => <tr key={issueKey(issue)}><td>{issue.rowNo}</td><td>{issue.message}</td></tr>)}</tbody></table></div>}
      {validationMessage ? <p className={checked ? "form-message" : "form-error"}>{validationMessage}</p> : rows.length > 0 && !allIssues.length && !missingCatalogs.length ? <p className="form-message">Tệp có {rows.length.toLocaleString("vi-VN")} dòng hợp lệ trên máy. Bấm “Kiểm tra tệp” để xác nhận trước khi lưu tồn kho.</p> : null}
      <div className="modal-actions"><Button type="button" variant="outline" onClick={onClose} disabled={busy}>Hủy</Button><Button type="button" variant="outline" disabled={busy || !rows.length || !!localIssues.length || !!catalogIssues.length || !!missingCatalogs.length} onClick={() => void validateOnServer()}>Kiểm tra tệp</Button><Button type="button" loading={busy && checked} disabled={busy || !checked || !!allIssues.length || !!missingCatalogs.length} onClick={() => void save()}><Upload size={16} /> Lưu tồn kho</Button></div>
    </div>
  </Modal>;
}

function validateImportRows(rows: ImportRow[]): ImportIssue[] {
  const issues: ImportIssue[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    if (!row.materialCode) issues.push({ rowNo: row.rowNo, message: "Thiếu Mã vật tư." });
    if (!row.warehouseCode) issues.push({ rowNo: row.rowNo, message: "Thiếu Mã kho." });
    if (!row.unitCode) issues.push({ rowNo: row.rowNo, message: "Thiếu Mã đơn vị tính." });
    if (!parseQuantity(row.quantity)) issues.push({ rowNo: row.rowNo, message: "Tồn kho phải là số từ 0 trở lên." });
    if (!row.snapshotAt) issues.push({ rowNo: row.rowNo, message: "Thiếu hoặc sai Thời điểm chốt." });
    const key = `${row.materialCode}:${row.warehouseCode}:${row.unitCode}:${row.snapshotAt}`.toLocaleLowerCase("vi");
    if (seen.has(key)) issues.push({ rowNo: row.rowNo, message: "Trùng vật tư, kho, đơn vị tính và thời điểm chốt trong tệp." });
    seen.add(key);
  }
  return issues;
}

function InventoryTemplateButton() {
  const [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    try {
      const XLSX = await import("xlsx-js-style");
      const sheet = XLSX.utils.aoa_to_sheet([inventoryTemplateColumns]);
      applyWorksheetStyle(XLSX, sheet, { headerRows: [0], dataStartsAt: 1, columnWidths: [18, 30, 16, 28, 14, 20, 16, 22, 32] });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Tồn kho");
      styleWorkbook(XLSX, workbook, "Mẫu nhập tồn kho");
      XLSX.writeFile(workbook, "mau-nhap-ton-kho.xlsx");
    } finally { setBusy(false); }
  }
  return <Button variant="outline" disabled={busy} onClick={() => void download()}><Download size={16} /> Tải mẫu Excel</Button>;
}

async function loadAllInventory(warehouseId: string) {
  const first = await api<InventoryResponse>(`/inventory?page=1&pageSize=100${warehouseId ? `&warehouseId=${warehouseId}` : ""}`);
  if (first.meta.totalPages <= 1) return first.items;
  const rest = await Promise.all(Array.from({ length: first.meta.totalPages - 1 }, (_, index) => api<InventoryResponse>(`/inventory?page=${index + 2}&pageSize=100${warehouseId ? `&warehouseId=${warehouseId}` : ""}`)));
  return [...first.items, ...rest.flatMap((result) => result.items)];
}

function InventoryExportButton({ warehouseId }: { warehouseId: string }) {
  const [busy, setBusy] = useState(false);
  async function exportFile() {
    setBusy(true);
    try {
      const rows = await loadAllInventory(warehouseId);
      const XLSX = await import("xlsx-js-style");
      const sheet = XLSX.utils.json_to_sheet(rows.map((row) => ({
        "Mã vật tư": row.material.code,
        "Tên vật tư": row.material.name,
        "Mã kho": row.warehouse.code,
        "Tên kho": row.warehouse.name,
        "Mã ĐVT": row.unit.code,
        "Tên ĐVT": row.unit.name,
        "Số chốt": row.quantity,
        "Còn lại": row.available,
        "Thời điểm chốt": new Date(row.snapshotAt),
        "Ghi chú": row.note,
      })));
      applyWorksheetStyle(XLSX, sheet, { headerRows: [0], dataStartsAt: 1, numberColumns: [6, 7], dateColumns: [8], columnWidths: [18, 38, 16, 28, 14, 20, 16, 16, 20, 32] });
      const workbook = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(workbook, sheet, "Tồn kho"); styleWorkbook(XLSX, workbook, "Tồn kho hiện tại"); XLSX.writeFile(workbook, "ton-kho-hien-tai.xlsx");
    } finally { setBusy(false); }
  }
  return <Button variant="outline" disabled={busy} onClick={() => void exportFile()}><Download size={16} /> Xuất Excel</Button>;
}
